import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { Provider, useSelector } from 'react-redux';
import { store } from './store';
import client from './apollo/client';
// Pages
import BottomNavigation from './components/navigation/BottomNavigation'
import HomePage from './pages/HomePage/HomePage'
import Yatzy from './pages/Yatzy/Yatzy'
import Landskamp from './pages/Landskamp/Landskamp'
import Kampdag from './pages/Kampdag/Kampdag'
import ChampionsLeagueGame from './pages/ChampionsLeagueGame/ChampionsLeagueGame'
import WorldCup2026 from './pages/WorldCup2026/WorldCup2026'
import AdminPanel from './pages/AdminPanel/AdminPanel'
import MateDetail from './pages/AdminPanel/MateManagement/MateDetail/MateDetail'
import Login from './pages/Login/Login'
import Profile from './pages/Profile/Profile'
import { LoginModalProvider, useLoginModal } from './components/LoginModalContext'
import ReduxInitializer from './components/ReduxInitializer'
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute'
import Signup from './pages/Signup/Signup'
import ResetPassword from './pages/ResetPassword/ResetPassword'
import DataPreloader from './components/DataPreloader'
import PwaInstallPrompt from './components/PwaInstallPrompt/PwaInstallPrompt'
import AutoEnablePush from './components/AutoEnablePush/AutoEnablePush'
import Fodboldhold from './pages/Fodboldhold/Fodboldhold'
import { TeamProvider } from './pages/Fodboldhold/store'
// styling
import './App.css'

/**
 * Hovedkomponenten for applikationen.
 * 
 * @component
 * @description
 * App komponenten er root komponenten for applikationen og håndterer:
 * - Apollo Client integration for GraphQL
 * - Routing med React Router
 * - Bundnavigation
 * - Hovedindhold via Routes
 * 
 * @returns {JSX.Element} Applikationens root komponent
 */
function LoginModalWrapper() {
  const { isLoginOpen, closeLogin } = useLoginModal();
  const loginInitialPassword = useSelector((state) => state.ui.loginInitialPassword);
  return isLoginOpen ? <Login onClose={closeLogin} initialPassword={loginInitialPassword} /> : null;
}

function AppShell() {
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.mate.currentUser);
  const isLoggedIn = isAuthenticated && !!token && !!user;
  const isAuthEntry = /^\/(signup|reset-password|hold\/inviter)\//.test(location.pathname);
  const isPublicYatzy = location.pathname === '/yatzy';

  if (!isLoggedIn && isPublicYatzy) {
    return (
      <div className="App app-guest">
        <Routes>
          <Route path="/yatzy" element={<Yatzy />} />
        </Routes>
      </div>
    );
  }

  if (!isLoggedIn && !isAuthEntry) {
    return (
      <div className="App app-locked">
        <Login />
      </div>
    );
  }

  return (
    <div className="App">
              {/* DataPreloader kører i baggrunden og preloader data baseret på brugerens position */}
              <DataPreloader />
              <AutoEnablePush />
              <TeamProvider>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/hold/*" element={<Fodboldhold />} />
                <Route path="/spil" element={<HomePage />} />
                <Route
                  path="/yatzy"
                  element={
                    <Yatzy />
                  }
                />
                <Route
                  path="/landskamp"
                  element={
                    <ProtectedRoute requiredPage="landskamp">
                      <Landskamp />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kampdag"
                  element={
                    <ProtectedRoute requiredPage="kampdag">
                      <Kampdag />
                    </ProtectedRoute>
                  }
                />
                <Route path="/signup/:token" element={<Signup />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route 
                  path="/champions-league" 
                  element={
                    <ProtectedRoute requiredPage="championsLeague">
                      <ChampionsLeagueGame />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/world-cup-2026"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/teams"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/matches"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/my-guesses"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/leaderboard"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/leagues"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/world-cup-2026/world-champion"
                  element={
                    <ProtectedRoute requiredPage="worldCup2026">
                      <WorldCup2026 />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminPanel />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/mate/:id" 
                  element={
                    <ProtectedRoute>
                      <MateDetail />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
              <BottomNavigation />
              </TeamProvider>
              <PwaInstallPrompt />
              <LoginModalWrapper />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ApolloProvider client={client}>
        <ReduxInitializer>
          <LoginModalProvider>
            <AppShell />
          </LoginModalProvider>
        </ReduxInitializer>
      </ApolloProvider>
    </Provider>
  )
}

export default App 